import { differenceInDays, subDays } from 'date-fns';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import { SCButton, SCDatepicker } from 'rollup-allspark';
import InfoModal from '../../../components/InfoModal';
import {
  ANALYTICS_MODULES,
  DATE_FILTER_DAY_LIMIT,
  MODULE_LIST,
} from '../../../config/constants/analytics-v2';

import { NATIVE_ANALYTICS_SAGA } from '../../../config/constants/analytics';
import {
  DEFAULT_PAGE_NO,
  DEFAULT_PAGE_SIZE,
} from '../../../config/constants/pagination';
import useAnalytics from '../../../hooks/useAnalytics';
import { cn, isEmpty } from '../../../utils';
import { subtractDays } from '../../../utils/format-date';
import {
  getEntity,
  getList,
  loadAnalyticsData,
  loadBusyModeAnalyticsData,
  loadBusyModeLogData,
  loadCashShiftsData,
  loadItemBreakdownData,
  loadList,
  loadOrdersData,
  loadMirrorOrdersData,
  loadPaymentMethodData,
  loadSummary,
  updateEntity,
} from '../actions';
import { PROMO_LIST, PROMO_SUMMARY } from '../sagaMap';
import { makeSelectProperty } from '../selectors';
import EntitySelector from './EntitySelector';

const Filters = props => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(true);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);

  const { getAnalyticsParams } = useAnalytics();

  const {
    isLoading = 0,
    getAnalyticsData,
    getBusyModeAnalyticsData,
    getBusyModeLogData,
    getItemBreakdownData,
    getOOSAnalytics,
    getCashShiftsData,
    getOrdersData,
    getPaymentMethodData,
    getMirrorOrdersData,
    getPromoAnalytics,
    getPromoSummary,
    getSummary,
    selectedBusiness,
    selectedBranch,
    selectedDateRange,
    selectedMenuReportType,
    selectedTab,
    selectedTimePeriod,
    savedNonCashBranchSelection,
    updateField,
  } = props;

  const handleSelectDateRange = dateRange => {
    const { data } = dateRange || {};

    if (data[0] && data[1]) {
      updateField({
        selectedDateRange: {
          start: data[0],
          end: data[1],
        },
      });
    }
  };

  const onTabClicked = module => {
    if (selectedTab === module.key) return;

    const isEnteringCash =
      module.key === ANALYTICS_MODULES.CASH &&
      selectedTab !== ANALYTICS_MODULES.CASH;
    const isLeavingCash = selectedTab === ANALYTICS_MODULES.CASH;
    const restoredBranches =
      isLeavingCash && !isEmpty(savedNonCashBranchSelection)
        ? savedNonCashBranchSelection
        : null;

    const updates = { selectedTab: module.key };
    if (isEnteringCash && !isEmpty(selectedBranch)) {
      updates.savedNonCashBranchSelection = selectedBranch;
      updates.selectedBranch = [];
    } else if (restoredBranches) {
      updates.selectedBranch = restoredBranches;
      updates.savedNonCashBranchSelection = [];
    }

    let paramOverrides = null;
    if (restoredBranches) {
      paramOverrides = { selectedBranch: restoredBranches };
    } else if (isEnteringCash) {
      paramOverrides = { selectedBranch: [] };
    }

    updateField(updates);
    onFilterApplied(module.key, paramOverrides);
  };

  const onFilterApplied = (tab = selectedTab, paramOverrides) => {
    if (isEmpty(selectedBusiness)) return;

    const params = getAnalyticsParams(paramOverrides);

    if (tab === ANALYTICS_MODULES.OVERVIEW) {
      getSummary(params);
    } else if (tab === ANALYTICS_MODULES.SALES) {
      getPaymentMethodData(params);
      getAnalyticsData(params);
    } else if (tab === ANALYTICS_MODULES.OPERATIONS) {
      getBusyModeAnalyticsData(params);
      getBusyModeLogData(params);

      const itemBreakdownParam = {
        ...params,
        activeTab: selectedMenuReportType.id,
      };
      getItemBreakdownData(itemBreakdownParam);

      getOOSAnalytics({
        url: NATIVE_ANALYTICS_SAGA.OOS_REPORT_ITEM.GET,
        params,
        reducerKey: NATIVE_ANALYTICS_SAGA.OOS_REPORT_ITEM.KEY,
      });

      // getMenuReportData();
      // getOOSAnalyticsData();
      // getPauseLogData();
    } else if (tab === ANALYTICS_MODULES.PROMO) {
      getPromoSummary({
        ...PROMO_SUMMARY,
        params: {
          ...params,
          business_id: selectedBusiness.id,
        },
        callbackFn: resp => {
          updateField({
            promoSummary: resp || {},
          });
        },
      });
      getPromoAnalytics({
        ...PROMO_LIST,
        params: {
          ...params,
          business_id: selectedBusiness.id,
        },
        callbackFn: resp => {
          updateField({
            promoAnalytics: resp || [],
          });
        },
      });
    } else if (tab === ANALYTICS_MODULES.ORDERS) {
      const numOfDays = differenceInDays(
        selectedDateRange?.end,
        selectedDateRange?.start,
      );
      let dateRange = selectedDateRange;
      if (numOfDays > 7) {
        setIsInfoModalOpen(true);
        dateRange = {
          start: subDays(new Date(), 6),
          end: new Date(),
        };
        updateField({
          selectedDateRange: dateRange,
        });
      }

      const orderParams = {
        ...params,
        page_no: DEFAULT_PAGE_NO,
        page_size: DEFAULT_PAGE_SIZE,
      };

      getOrdersData(orderParams);
      getMirrorOrdersData(orderParams);
    } else if (tab === ANALYTICS_MODULES.CASH) {
      const cashParams = {
        ...params,
        business_id: selectedBusiness?.id,
        page_no: DEFAULT_PAGE_NO,
        page_size: DEFAULT_PAGE_SIZE,
      };

      getCashShiftsData(cashParams);
    }
  };

  const selectorEl = isDropdownOpen ? (
    <EntitySelector onFilterApplied={() => onFilterApplied()} />
  ) : null;

  const tabEl = MODULE_LIST.map(module => (
    <div
      key={module.key}
      className={cn({
        'py-2 border-b-2 cursor-pointer font-medium text-center': true,
        [module.customClass]: !isEmpty(module.customClass),
        'border-primary text-primary': selectedTab === module.key,
      })}
      role="presentation"
      onClick={() => onTabClicked(module)}
    >
      {module.title}
    </div>
  ));

  return (
    <div className="flex flex-col p-4 bg-white border border-grey rounded-lg gap-y-4">
      <div className="flex items-start justify-between">
        <div className="flex">{tabEl}</div>
        <div className="flex flex-1 items-start justify-end gap-x-4">
          <div className="flex flex-col -my-4 w-64">
            <SCDatepicker
              helpText="Select Range"
              elementType="input"
              placeholder="Select date range"
              isClearable={false}
              selectsRange
              startDate={selectedDateRange?.start}
              endDate={selectedDateRange?.end}
              dateFormat="yyyy-MM-dd"
              displayFormat="MMM d, yyyy"
              maxDaysLimit={
                selectedTab === ANALYTICS_MODULES.ORDERS
                  ? 7
                  : selectedTab === ANALYTICS_MODULES.CASH
                  ? 30
                  : 95
              }
              minDate={subtractDays(new Date(), DATE_FILTER_DAY_LIMIT - 1)}
              maxDate={new Date()}
              disableLabel
              onSelect={handleSelectDateRange}
            />
          </div>

          <SCButton
            variant="secondary"
            action={() => setIsDropdownOpen(!isDropdownOpen)}
            className={cn({
              'w-auto': true,
              'bg-primary-lighter text-primary hover:bg-primary hover:text-white':
                !isDropdownOpen,
              'bg-grey-light text-black hover:bg-grey': isDropdownOpen,
            })}
          >
            <i className="kt-filter" />
          </SCButton>

          <SCButton
            variant="primary"
            action={() => onFilterApplied()}
            className="w-auto"
            disabled={
              isEmpty(selectedBusiness) ||
              isEmpty(selectedDateRange) ||
              !selectedDateRange?.start ||
              !selectedDateRange?.end ||
              isEmpty(selectedTimePeriod) ||
              !!isLoading
            }
          >
            <i className="kt-check-mark" />
          </SCButton>
        </div>
      </div>
      {selectorEl}
      <InfoModal
        isOpen={isInfoModalOpen}
        title="Date Range Limit"
        content={
          <p className="text-black text-opacity-75 text-base leading-6">
            For <span className="font-semibold">Orders</span>, the maximum date
            range is <span className="font-semibold">7</span> days, so the
            selected date range has been adjusted to the last
            <span className="font-semibold"> 7</span> days.
          </p>
        }
        handleClose={() => setIsInfoModalOpen(false)}
        closeBtnLabel="Okay"
      />
    </div>
  );
};

Filters.propTypes = {
  isLoading: PropTypes.number,
  selectedBusiness: PropTypes.object,
  selectedDateRange: PropTypes.object,
  selectedMenuReportType: PropTypes.object,
  selectedTab: PropTypes.string,
  selectedTimePeriod: PropTypes.object,
  updateField: PropTypes.func,

  getAnalyticsData: PropTypes.func,
  getBusyModeAnalyticsData: PropTypes.func,
  getBusyModeLogData: PropTypes.func,
  getItemBreakdownData: PropTypes.func,
  getOOSAnalytics: PropTypes.func,
  getCashShiftsData: PropTypes.func,
  getOrdersData: PropTypes.func,
  getPaymentMethodData: PropTypes.func,
  getPromoAnalytics: PropTypes.func,
  getPromoSummary: PropTypes.func,
  getSummary: PropTypes.func,
};

const mapStateToProps = createStructuredSelector({
  branches: makeSelectProperty('branches'),
  branchMap: makeSelectProperty('branchMap'),
  brands: makeSelectProperty('brands'),
  brandMap: makeSelectProperty('brandMap'),
  isLoading: makeSelectProperty('isLoading'),
  orderSummary: makeSelectProperty('orderSummary'),
  providerMap: makeSelectProperty('providerMap'),
  providers: makeSelectProperty('analyticsProviders'),
  selectedBusiness: makeSelectProperty('selectedBusiness'),
  selectedBranch: makeSelectProperty('selectedBranch'),
  selectedDateRange: makeSelectProperty('selectedDateRange'),
  selectedMenuReportType: makeSelectProperty('selectedMenuReportType'),
  selectedTab: makeSelectProperty('selectedTab'),
  savedNonCashBranchSelection: makeSelectProperty(
    'savedNonCashBranchSelection',
  ),
  selectedTimePeriod: makeSelectProperty('selectedTimePeriod'),
  selectedTimeRange: makeSelectProperty('selectedTimeRange'),
});

const mapDispatchToProps = dispatch => ({
  getItemBreakdownData: params => dispatch(loadItemBreakdownData(params)),
  getMenuReport: meta => dispatch(getEntity(meta)),
  getOrderBreakdown: meta => dispatch(getList(meta)),
  getOrderMetric: meta => dispatch(getList(meta)),
  getOrderSummary: meta => dispatch(getEntity(meta)),
  getPauseLog: meta => dispatch(getList(meta)),
  getPaymentMethodAnalytics: meta => dispatch(getList(meta)),
  updateField: field => dispatch(updateEntity(field)),

  getAnalyticsData: params => dispatch(loadAnalyticsData(params)),
  getBusyModeAnalyticsData: params =>
    dispatch(loadBusyModeAnalyticsData(params)),
  getBusyModeLogData: params => dispatch(loadBusyModeLogData(params)),
  getCashShiftsData: params => dispatch(loadCashShiftsData(params)),
  getOOSAnalytics: params => dispatch(getList(params)),
  getOrdersData: params => dispatch(loadOrdersData(params)),
  getMirrorOrdersData: params => dispatch(loadMirrorOrdersData(params)),
  getPaymentMethodData: params => dispatch(loadPaymentMethodData(params)),
  getPromoSummary: params => dispatch(getEntity(params)),
  getPromoAnalytics: params => dispatch(loadList(params)),
  getSummary: params => dispatch(loadSummary(params)),
});

const withConnect = connect(mapStateToProps, mapDispatchToProps);

export default compose(withConnect)(Filters);
